import React, { useState, useMemo } from 'react'; // Добавлен useState
import {
  Card, CardContent, Typography, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, CircularProgress, List, ListItem, ListItemText, TableSortLabel,
  Collapse, FormControl, FormLabel, TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

import { useRecoilValue } from 'recoil';
import { optimalWavesState, simpleStatsState, waveClusterStatsState, waveClusterOutcomesState } from '#state/trades';
import { strategiesConfig } from '#state/settings';
import { calculateFadeExpectancy } from '#state/utils';

// const defaultFee = 0.001 // 0.00065 // 0.00026
const marketFee = 0.0005 // 0.0005
const limitFee = 0.0002 // 0.0002
const cashbackRate = 0.35

function formatValue(num, significantDigits = 3, isPercent = false) {
  // ... (ваш код formatValue без изменений)
  if (typeof num !== 'number' || isNaN(num)) return 'N/A';
  if (isPercent) num = num * 100
  const suffixes = { 1000: 'K', 1000000: 'M', 1000000000: 'B', 1000000000000: 'T' };
  const absNum = Math.abs(num);
  const sigDelta = Math.max(0, significantDigits - 3)
  if (absNum >= 1000) {
    let scale = 1;
    let suffix = '';
    let scaledNum = num;
    while (scaledNum >= 1000 && scale <= 1e12) {
      scale *= 1000;
      scaledNum = num / scale;
      suffix = suffixes[scale] || '';
    }
    if (scaledNum >= 100) { return (scaledNum.toFixed(sigDelta) + suffix); }
    else if (scaledNum >= 10) { return (scaledNum.toFixed(sigDelta + 1) + suffix); }
    else { return (scaledNum.toFixed(sigDelta + 2) + suffix); }
  } else if (absNum < 1) {
    return num.toPrecision(significantDigits);
  } else {
    if (num >= 100) { return num.toFixed(sigDelta); }
    else if (num >= 10) { return num.toFixed(sigDelta + 1); }
    else { return num.toFixed(sigDelta + 2); }
  }
}

export const columnsConfig = [
  {
    id: 'name',
    label: 'Category',
    align: 'left',
    formatter: (data, rowConfig) => <strong>{rowConfig.label}</strong>,
  },
  {
    id: 'count',
    label: 'Count',
    align: 'right',
    formatter: (data) => data?.count,
  },
  {
    id: 'avgDeltaPercent',
    label: 'Avg Delta (%)',
    align: 'right',
    formatter: (data) => `${formatValue(data?.deltaPercent?.avg, 2, true)}%`,
  },
  {
    id: 'medDeltaPercent',
    label: 'Med Delta (%)',
    align: 'right',
    formatter: (data) => `${formatValue(data?.deltaPercent?.median, 2, true)}%`,
  },
  {
    id: 'avgDeltaPRatio',
    label: 'Avg Delta (%) Ratio',
    align: 'right',
    formatter: (data) => formatValue(data?.deltaRatioPrev?.avg, 2),
  },
  {
    id: 'medDeltaPRatio',
    label: 'Med Delta (%) Ratio',
    align: 'right',
    formatter: (data) => formatValue(data?.deltaRatioPrev?.median, 2),
  },
  {
    id: 'avgVolume',
    label: 'Avg Volume',
    align: 'right',
    formatter: (data) => formatValue(data?.totalVolume?.avg, 3),
  },
];

export const rowsConfig = [
  // --- General Stats ---
  { id: 'overall', label: 'Overall', level: 0, dataAccessor: (stats) => stats.overall },
  { id: 'overallUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.overall?.up },
  { id: 'overallDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.overall?.down },

  // --- Move Waves ---
  { id: 'move', label: 'Move', level: 0, dataAccessor: (stats) => stats.move },
  { id: 'moveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.move?.up },
  { id: 'moveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.move?.down },

  // --- Pullback Waves ---
  { id: 'pullback', label: 'Pullback', level: 0, dataAccessor: (stats) => stats.pullback },
  { id: 'pullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.pullback?.up },
  { id: 'pullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.pullback?.down },

  // --- "Pre" Sequences (Describes the wave BEFORE the event) ---
  { id: 'pullbackPreMove', label: 'Pre: Pullback before Move', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPreMove },
  { id: 'pullbackPreMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPreMove?.up },
  { id: 'pullbackPreMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPreMove?.down },

  { id: 'movePrePullback', label: 'Pre: Move before Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.movePrePullback },
  { id: 'movePrePullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePrePullback?.up },
  { id: 'movePrePullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePrePullback?.down },

  { id: 'pullbackPrePullback', label: 'Pre: Pullback before Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback },
  { id: 'pullbackPrePullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback?.up },
  { id: 'pullbackPrePullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPrePullback?.down },

  { id: 'movePreMove', label: 'Pre: Move before Move', level: 0, dataAccessor: (stats) => stats.sequences?.movePreMove },
  { id: 'movePreMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePreMove?.up },
  { id: 'movePreMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePreMove?.down },

  // --- "Post" Sequences (Describes the wave AFTER the event) ---
  { id: 'movePostPullback', label: 'Post: Move after Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.movePostPullback },
  { id: 'movePostPullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePostPullback?.up },
  { id: 'movePostPullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePostPullback?.down },

  { id: 'pullbackPostMove', label: 'Post: Pullback after Move', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPostMove },
  { id: 'pullbackPostMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostMove?.up },
  { id: 'pullbackPostMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostMove?.down },

  { id: 'pullbackPostPullback', label: 'Post: Pullback after Pullback', level: 0, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback },
  { id: 'pullbackPostPullbackUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback?.up },
  { id: 'pullbackPostPullbackDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.pullbackPostPullback?.down },

  { id: 'movePostMove', label: 'Post: Move after Move', level: 0, dataAccessor: (stats) => stats.sequences?.movePostMove },
  { id: 'movePostMoveUp', label: '↳ Up', level: 1, dataAccessor: (stats) => stats.sequences?.movePostMove?.up },
  { id: 'movePostMoveDown', label: '↳ Down', level: 1, dataAccessor: (stats) => stats.sequences?.movePostMove?.down },
];

export const comparisonConfig = {
  // --- СПИСОК МЕТРИК ДЛЯ СРАВНЕНИЯ ---
  metrics: [
    { name: 'Count', accessor: (d) => d?.count },
    { name: 'Avg xRatio', accessor: (d) => d?.deltaRatioPrev?.avg },
    { name: 'Avg Delta (%)', accessor: (d) => d?.deltaPercent?.avg, isPercent: true },
    { name: 'Med Delta (%)', accessor: (d) => d?.deltaPercent?.median, isPercent: true },
    { name: 'Avg Volume', accessor: (d) => d?.totalVolume?.avg },
    { name: 'Avg Effort', accessor: (d) => d?.effortRatio?.avg },
  ],

  // --- СПИСОК СРАВНЕНИЙ ---
  comparisons: [
    // =======================================================
    // ОБЩИЕ СРАВНЕНИЯ (БЕЗ РАЗДЕЛЕНИЯ ПО НАПРАВЛЕНИЮ)
    // =======================================================
    {
      title: 'Base',
      pathA: 'move', titleA: 'Move', // Исправлено
      pathB: 'pullback', titleB: 'Pullback' // Исправлено
    },
    {
      title: 'Pre: Move before',
      pathA: 'sequences.movePreMove', titleA: 'Move -> Move',
      pathB: 'sequences.movePrePullback', titleB: 'Move -> Pullback'
    },
    {
      title: 'Post: Move after',
      pathA: 'sequences.movePostMove', titleA: 'Move -> Move',
      pathB: 'sequences.movePostPullback', titleB: 'Pullback -> Move'
    },
    {
      title: 'Pre: Pullback before',
      pathA: 'sequences.pullbackPreMove', titleA: 'Pullback -> Move',
      pathB: 'sequences.pullbackPrePullback', titleB: 'Pullback -> Pullback'
    },
    {
      title: 'Post: Pullback after',
      pathA: 'sequences.pullbackPostMove', titleA: 'Move -> Pullback',
      pathB: 'sequences.pullbackPostPullback', titleB: 'Pullback -> Pullback'
    },

    // =======================================================
    // ВОСХОДЯЩИЙ ТРЕНД (UP)
    // =======================================================
    // {
    //   title: 'Base (Up)',
    //   pathA: 'move.up', titleA: 'Move Up',
    //   pathB: 'pullback.up', titleB: 'Pullback Up'
    // },
    // {
    //   title: 'Pre: Move before (Up)',
    //   pathA: 'sequences.movePreMove.up', titleA: 'Move Up -> Move Up',
    //   pathB: 'sequences.movePrePullback.up', titleB: 'Move Up -> Pullback Up'
    // },
    // {
    //   title: 'Pre: Pullback before (Up)',
    //   pathA: 'sequences.pullbackPreMove.up', titleA: 'Pullback Up -> Move Up',
    //   pathB: 'sequences.pullbackPrePullback.up', titleB: 'Pullback Up -> Pullback Up'
    // },
    // {
    //   title: 'Post: Move after (Up)',
    //   pathA: 'sequences.movePostMove.up', titleA: 'Move Up -> Move Up',
    //   pathB: 'sequences.movePostPullback.up', titleB: 'Pullback Up -> Move Up'
    // },
    // {
    //   title: 'Post: Pullback after (Up)',
    //   pathA: 'sequences.pullbackPostMove.up', titleA: 'Move Up -> Pullback Up',
    //   pathB: 'sequences.pullbackPostPullback.up', titleB: 'Pullback Up -> Pullback Up'
    // },

    // =======================================================
    // НИСХОДЯЩИЙ ТРЕНД (DOWN)
    // =======================================================
    // {
    //   title: 'Base (Down)',
    //   pathA: 'move.down', titleA: 'Move Down',
    //   pathB: 'pullback.down', titleB: 'Pullback Down'
    // },
    // {
    //   title: 'Pre: Move before (Down)',
    //   pathA: 'sequences.movePreMove.down', titleA: 'Move Down -> Move Down',
    //   pathB: 'sequences.movePrePullback.down', titleB: 'Move Down -> Pullback Down'
    // },
    // {
    //   title: 'Pre: Pullback before (Down)',
    //   pathA: 'sequences.pullbackPreMove.down', titleA: 'Pullback Down -> Move Down',
    //   pathB: 'sequences.pullbackPrePullback.down', titleB: 'Pullback Down -> Pullback Down'
    // },
    // {
    //   title: 'Post: Move after (Down)',
    //   pathA: 'sequences.movePostMove.down', titleA: 'Move Down -> Move Down',
    //   pathB: 'sequences.movePostPullback.down', titleB: 'Pullback Down -> Move Down'
    // },
    // {
    //   title: 'Post: Pullback after (Down)',
    //   pathA: 'sequences.pullbackPostMove.down', titleA: 'Move Down -> Pullback Down',
    //   pathB: 'sequences.pullbackPostPullback.down', titleB: 'Pullback Down -> Pullback Down'
    // },

    // =======================================================
    // СРАВНЕНИЕ НАПРАВЛЕНИЙ (UP vs DOWN)
    // =======================================================
    // {
    //   title: 'Directional: Move',
    //   pathA: 'move.up', titleA: 'Move Up',
    //   pathB: 'move.down', titleB: 'Move Down'
    // },
    // {
    //   title: 'Directional: Pullback',
    //   pathA: 'pullback.up', titleA: 'Pullback Up',
    //   pathB: 'pullback.down', titleB: 'Pullback Down'
    // },
    // {
    //   title: 'Directional: Move -> Move',
    //   pathA: 'sequences.movePreMove.up', titleA: 'Move Up -> Move Up',
    //   pathB: 'sequences.movePreMove.down', titleB: 'Move Down -> Move Down'
    // },
    // {
    //   title: 'Directional: Pullback -> Pullback',
    //   pathA: 'sequences.pullbackPrePullback.up', titleA: 'Pullback Up -> Pullback Up',
    //   pathB: 'sequences.pullbackPrePullback.down', titleB: 'Pullback Down -> Pullback Down'
    // },
  ]
};

// **********************************
// INDICATORS
// **********************************

/**
 * Карточка 3: Сравнение импульсов Вверх и Вниз.
 */
const AsymmetryCard = ({ stats }) => {
  const data = useMemo(() => {
    const moveUp = stats.move?.up;
    const moveDown = stats.move?.down;
    const pullbackUp = stats.pullback?.up;
    const pullbackDown = stats.pullback?.down;
    if (!moveUp || !moveDown || !pullbackUp || !pullbackDown) return null;
    return {
      moveUp: {
        name: 'Move Up',
        strength: formatValue(moveUp.deltaRatioPrev?.avg, 2),
        volume: formatValue(moveUp.totalVolume?.avg, 3)
      },
      moveDown: {
        name: 'Move Down',
        strength: formatValue(moveDown.deltaRatioPrev?.avg, 2),
        volume: formatValue(moveDown.totalVolume?.avg, 3)
      },
      pullbackUp: {
        name: 'Pullback Up',
        strength: formatValue(pullbackUp.deltaRatioPrev?.avg, 2),
        volume: formatValue(pullbackUp.totalVolume?.avg, 3)
      },
      pullbackDown: {
        name: 'Pullback Down',
        strength: formatValue(pullbackDown.deltaRatioPrev?.avg, 2),
        volume: formatValue(pullbackDown.totalVolume?.avg, 3)
      }

    };
  }, [stats]);

  if (!data) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h3" gutterBottom>Asymmetry Up vs. Down</Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Метрика</TableCell>
                <TableCell align="right">{data.moveUp.name}</TableCell>
                <TableCell align="right">{data.moveDown.name}</TableCell>
                <TableCell align="right">{data.pullbackUp.name}</TableCell>
                <TableCell align="right">{data.pullbackDown.name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Средняя сила (xRatio)</TableCell>
                <TableCell align="right">{data.moveUp.strength}</TableCell>
                <TableCell align="right">{data.moveDown.strength}</TableCell>
                <TableCell align="right">{data.pullbackUp.strength}</TableCell>
                <TableCell align="right">{data.pullbackDown.strength}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Средний объем</TableCell>
                <TableCell align="right">{data.moveUp.volume}</TableCell>
                <TableCell align="right">{data.moveDown.volume}</TableCell>
                <TableCell align="right">{data.pullbackUp.volume}</TableCell>
                <TableCell align="right">{data.pullbackDown.volume}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};


const MarketRegimeIndicator = ({ stats }) => {
  const { pullbackPreMove, pullbackPrePullback } = stats.sequences || {};
  // Рассчитываем режим только если есть достаточно данных
  const regime = useMemo(() => {
    if (!pullbackPreMove || !pullbackPrePullback || (pullbackPreMove.count + pullbackPrePullback.count < 10)) {
      return { label: 'НЕДОСТАТОЧНО ДАННЫХ', color: 'default', Icon: InfoIcon };
    }
    const trendProbability = pullbackPreMove.count / (pullbackPreMove.count + pullbackPrePullback.count);
    if (trendProbability > 0.6) {
      return { label: 'Здоровый Тренд', color: 'success.main', Icon: CheckCircleIcon };
    }
    if (trendProbability < 0.45) {
      return { label: 'Боковик / Чоп', color: 'warning.main', Icon: WarningIcon };
    }
    return { label: 'Неопределенность', color: 'text.secondary', Icon: InfoIcon };
  }, [stats]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
      <regime.Icon sx={{ color: regime.color, mr: 1, fontSize: '1.2rem' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        Режим рынка: <Box component="span" sx={{ color: regime.color }}>{regime.label}</Box>
      </Typography>
    </Box>
  );
};

const StrategyCard = ({ title, setupAnatomy, expectancy }) => {
  // Внутренний компонент для строки в блоке матожидания
  const ExpectancyRow = ({ label, value, color = 'text.primary', isBold = false, unit = '' }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
      <Typography variant="body2" color={color === 'text.primary' ? 'text.secondary' : color}>{label}:</Typography>
      <Typography variant="body2" sx={{ fontWeight: isBold ? 'bold' : 'normal', color }}>
        {value}{unit}
      </Typography>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>{title}</Typography>
        <Grid container spacing={1}>
          {/* Раздел А: Анатомия сетапа */}
          {setupAnatomy ?
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Strategy Key Stats</Typography>
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">{setupAnatomy.good.name}</TableCell>
                      <TableCell align="right">{setupAnatomy.bad.name}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {setupAnatomy.metrics.map(m => (
                      <TableRow key={m.label}>
                        <TableCell>{m.label}</TableCell>
                        <TableCell align="right"><strong>{m.goodValue}</strong></TableCell>
                        <TableCell align="right">{m.badValue}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid> : null}

          {/* Раздел Б: Оценка эффективности (полностью переделан) */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Expected Efficiency</Typography>
            <Paper elevation={2} sx={{ p: 1.5 }}>
              <ExpectancyRow label="Вероятность успеха (Win Rate)" value={expectancy.winRate.toFixed(1)} unit="%" isBold={true} />
              <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)', my: 1 }} />
              <ExpectancyRow label="Средний выигрыш (Median)" value={`+${formatValue(expectancy.avgWin, 2, true)}`} unit="%" />
              <ExpectancyRow label="Средний убыток (Median)" value={`-${formatValue(expectancy.avgLoss, 2, true)}`} unit="%" />
              <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)', my: 1 }} />
              <ExpectancyRow label="Матожидание (без комиссий)" value={`${formatValue(expectancy.grossExpectancy, 3, true)}`}
                unit="%" color="text.secondary" />
              <ExpectancyRow label="Матожидание (Net 0.2%)" value={`${formatValue(expectancy.netExpectancy, 3, true)}`}
                unit="%" color={expectancy.netExpectancy > 0 ? 'success.main' : 'error.main'} isBold={true} />
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};


const StrategyExpectancyTable = ({ results }) => {
  // Внутренние хелперы и логика для сортировки (остаются без изменений)
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('netExpectancy');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = useMemo(() => {
    const comparator = (a, b) => {
      if (b.expectancy[orderBy] < a.expectancy[orderBy]) return -1;
      if (b.expectancy[orderBy] > a.expectancy[orderBy]) return 1;
      return 0;
    };
    const stabilizedThis = results.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderValue = comparator(a[0], b[0]);
      if (orderValue !== 0) return order === 'desc' ? orderValue : -orderValue;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }, [order, orderBy, results]);

  // Определяем заголовки для новой, правильной таблицы
  const headCells = [
    { id: 'title', label: 'Strategy', numeric: false },
    { id: 'netExpectancy', label: 'Expectancy (Net %)', numeric: true },
    { id: 'rrRatio', label: 'R/R Ratio', numeric: true },
    { id: 'winRate', label: 'Win Rate (%)', numeric: true },
    { id: 'avgWin', label: 'Avg Win (%)', numeric: true },
    { id: 'avgLoss', label: 'Avg Loss (%)', numeric: true }
  ];

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedResults.map((result) => {
              const { expectancy, title } = result;
              // --- ДОБАВЛЕН РАСЧЕТ R/R ---
              const rrRatio = expectancy.avgLoss !== 0 ? expectancy.avgWin / expectancy.avgLoss : Infinity;

              return (
                <TableRow hover key={title}>
                  <TableCell component="th" scope="row">
                    {title}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 'bold',
                    color: expectancy.netExpectancy > 0 ? 'success.main' : 'error.main'
                  }}>
                    {formatValue(expectancy.netExpectancy, 3, true)}%
                  </TableCell>
                  <TableCell align="right">
                    {isFinite(rrRatio) ? formatValue(rrRatio, 2) : 'N/A'}
                  </TableCell>
                  <TableCell align="right">{(expectancy.winRate * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right" sx={{
                    color: expectancy.avgWin > 0 ? 'success.dark' : 'error.dark'
                  }}>
                    {formatValue(expectancy.avgWin, 2, true)}%
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.dark' }}>
                    -{formatValue(expectancy.avgLoss, 2, true)}%
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};



const StrategyDashboard = ({ stats }) => {
  const [stopMultiplier, setStopMultiplier] = useState(1);
  const [takeShift, setTakeShift] = useState(0);

  const handleMultiplierChange = (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) {
      value = 1;
    }
    if (value < 1) value = 1;
    if (value > 3) value = 3;
    setStopMultiplier(value);
  };

  const handleTakeShiftChange = (event) => {
    let value = parseFloat(event.target.value);
    if (isNaN(value)) {
      value = 0;
    }
    if (value < -3) value = -3;
    if (value > 3) value = 3;
    setTakeShift(value);
  };

  // --- Расчет данных для Стратегии №1: Вход после отката ---
  const pullbackContinuationExpectancy = useMemo(() => {
    const pMove = stats.sequences?.pullbackPreMove;
    const pPull = stats.sequences?.pullbackPrePullback;
    if (!pMove?.count || !pPull?.count) return null;

    const winRate = pMove.count / (pMove.count + pPull.count);

    // Используем точные данные об исходах
    const avgWin = stats.sequences.movePostPullback?.deltaPercent?.median;
    const avgLoss = stats.sequences.pullbackPostPullback?.deltaPercent?.median;
    if (avgWin === undefined || avgLoss === undefined) return null;

    const grossExpectancy = (winRate * avgWin) - ((1 - winRate) * avgLoss);

    return {
      winRate: winRate * 100,
      avgWin,
      avgLoss,
      grossExpectancy,
      netExpectancyLimit: grossExpectancy - 0.001,
      netExpectancy: grossExpectancy - 0.002,
    };
  }, [stats]);

  const pullbackAnatomy = useMemo(() => {
    const pMove = stats.sequences?.pullbackPreMove;
    const pPull = stats.sequences?.pullbackPrePullback;
    if (!pMove?.count || !pPull?.count) return null;

    return {
      good: { name: '"Хороший" откат' },
      bad: { name: '"Плохой" откат' },
      metrics: [
        {
          label: 'Медианный Размер',
          goodValue: `${formatValue(pMove.deltaPercent?.median, 2, true)}%`,
          badValue: `${formatValue(pPull.deltaPercent?.median, 2, true)}%`
        },
        {
          label: 'Медианный Объем',
          goodValue: formatValue(pMove.totalVolume?.median, 3),
          badValue: formatValue(pPull.totalVolume?.median, 3)
        },
        {
          label: 'Медианное Усилие',
          goodValue: formatValue(pMove.effortRatio?.median, 3),
          badValue: formatValue(pPull.effortRatio?.median, 3)
        },
      ]
    }
  }, [stats]);



  const strategyResults = useMemo(() => {
    return strategiesConfig
      .map(config => ({
        title: config.title,
        expectancy: calculateFadeExpectancy(stats, config, {
          commission: (marketFee * 2) * (1 - cashbackRate), // 0.002,
          stopMultiplier,
          takeShift,
          averagingMode: 'median'
        })
      }))
      .filter(result => result.expectancy !== null); // Убираем те, что не удалось рассчитать
  }, [stats, stopMultiplier, takeShift]);

  if (strategyResults.length === 0) {
    return <Typography sx={{ p: 2, textAlign: 'center' }}>Not enouth data</Typography>;
  }


  return (
    <Box sx={{ mb: 1 }}>
      {/* <MarketRegimeIndicator stats={stats} /> */}
      {/* {pullbackAnatomy && pullbackContinuationExpectancy &&
        <StrategyCard
          title="Стратегия: Вход после качественного отката"
          setupAnatomy={pullbackAnatomy}
          expectancy={pullbackContinuationExpectancy}
        />
      } */}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControl component="fieldset" sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}>
          {/* <FormLabel component="legend" sx={{ mb: 1 }}>Settings</FormLabel> */}
          <TextField
            label="SL multiplier"
            type="number"
            size="small"
            value={stopMultiplier}
            onChange={handleMultiplierChange}
            inputProps={{
              step: 1,
              min: 1,
              max: 3,
            }}
            sx={{ minWidth: '100px' }}
          />

          <TextField
            label="TP shift"
            type="number"
            size="small"
            value={takeShift}
            onChange={handleTakeShiftChange}
            inputProps={{
              step: 1,
              min: -3,
              max: 3,
            }}
            sx={{ minWidth: '100px' }}
          />
        </FormControl>
      </Paper>
      {
        strategyResults.length
          ? <StrategyExpectancyTable results={strategyResults} />
          : <Typography sx={{ p: 2, textAlign: 'center' }}>Not enouth data</Typography>
      }
    </Box>
  );
}

// **********************************
// STATS
// **********************************

// Вспомогательная функция для линейной интерполяции
const lerp = (start, end, amount) => {
  return Math.round(start + (end - start) * amount);
};

/**
 * Возвращает объект стиля для Ratio через интерполяцию цвета.
 * @param {number | null} ratio - Значение соотношения.
 * @returns {object} - Объект стиля для sx-пропа.
 */

const getRatioStyle = (ratio, imba) => {
  if (ratio === null || ratio === 0) {
    return { color: 'inherit', fontWeight: 'normal' };
  }

  // --- Настройки (остаются без изменений) ---
  const BASE_COLOR_RGB = [255, 255, 255];
  const DEVIATION_COLOR_RGB = [230, 38, 94];
  const MAX_IMBALANCE_THRESHOLD = 1.0;
  const MIN_DISPLAY_IMBALANCE = 0.01;
  // ---

  const imbalance = imba //Math.max(ratio, 1 / ratio) - 1;

  let color = 'inherit';
  let fontWeight = 'normal';

  if (imbalance > MIN_DISPLAY_IMBALANCE) {
    // 't' - это линейный коэффициент смешивания от 0.0 до 1.0
    let t = Math.min(1, imbalance / MAX_IMBALANCE_THRESHOLD);

    //  Применяем нелинейную Easing-функцию (квадратичную)
    // t = t * t;
    t = t * t * (3 - 2 * t);
    // t = 1 - Math.sqrt(1 - Math.pow(t, 2));

    // Интерполяция с использованием нового, нелинейного 't'
    const r = lerp(BASE_COLOR_RGB[0], DEVIATION_COLOR_RGB[0], t);
    const g = lerp(BASE_COLOR_RGB[1], DEVIATION_COLOR_RGB[1], t);
    const b = lerp(BASE_COLOR_RGB[2], DEVIATION_COLOR_RGB[2], t);

    color = `rgb(${r}, ${g}, ${b})`;
    // fontWeight = 'bold';
  }

  return { color, fontWeight };
};

const ComparisonMetric = ({ metricName, dataA, dataB, accessor, isPercent = false }) => {
  const valA = accessor(dataA);
  const valB = accessor(dataB);
  const ratio = (valB && valB !== 0) ? valA / valB : null;

  // const ratio = valueB > 0 ? valueA / valueB : Infinity;
  const imbalance = (ratio > 0 && isFinite(ratio)) ? Math.abs(Math.log(ratio)) : 0;

  // Получаем динамический стиль для Ratio
  const ratioStyle = getRatioStyle(ratio, imbalance);

  return (
    <Grid item xs={4} sm={4} md={2} lg={2}>
      <Paper elevation={2} sx={{
        p: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ flexGrow: 1 }}>
            {metricName}
          </Typography>
          <Typography variant="h6" component="p" sx={{ mb: 1, ...ratioStyle }}>
            {`Score: ${formatValue(imbalance, 2)}`}
          </Typography>
        </Box>
        {/* <Box>
          <Typography variant="body1" component="p">
            {ratio !== null ? `Ratio: ${formatValue(ratio, 2)}` : 'Ratio: N/A'}
          </Typography>
        </Box> */}
        <Box>
          <Typography variant="body2">
            {ratio !== null ? `Ratio: ${formatValue(ratio, 2)}` : 'Ratio: N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'warning.main' }}>
            A: <strong>{formatValue(valA, 3, isPercent)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'info.main' }}>
            B: <strong>{formatValue(valB, 3, isPercent)}</strong>
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
};


const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const ConfigurableComparisons = ({ stats, config }) => {
  if (!stats || !config || !config.comparisons || !config.metrics) {
    return null;
  }

  return (
    <Box sx={{ my: 1 }}>
      {config.comparisons.map((comp, index) => {
        // Получаем данные для A и B, используя пути из конфига
        const dataA = getNestedValue(stats, comp.pathA);
        const dataB = getNestedValue(stats, comp.pathB);

        return (
          <DualCategoryComparisonCard
            key={`${comp.title}-${index}`}
            title={comp.title}
            dataA={dataA}
            titleA={comp.titleA}
            dataB={dataB}
            titleB={comp.titleB}
            // Передаем массив метрик в каждую карточку
            metrics={config.metrics}
          />
        );
      })}
    </Box>
  );
};


export const DualCategoryComparisonCard = ({ title, dataA, titleA, dataB, titleB, metrics }) => {
  // Проверка на наличие данных остается прежней
  if (!dataA || dataA.count === 0 || !dataB || dataB.count === 0) {
    return (
      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent>
          {title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
          <Typography sx={{ p: 2, textAlign: 'center' }}>Not enough data to compare {titleA} vs {titleB}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Метрики теперь приходят из props, а не определяются внутри
  if (!metrics || metrics.length === 0) {
    return <Typography>Error: Metrics configuration is missing.</Typography>;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1 }}>
          {title ? <Typography variant="h6" mr={1}>{title}:</Typography> : null}
          <Typography variant="h6">
            <span style={{ color: '#ffb74d' }}>{titleA} (A)</span> vs <span style={{ color: '#64b5f6' }}>{titleB} (B)</span>
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {metrics.map(metric => (
            <ComparisonMetric
              key={metric.name}
              metricName={metric.name}
              dataA={dataA}
              dataB={dataB}
              accessor={metric.accessor}
              isPercent={metric.isPercent || false}
            />
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

const ComparisonCard = ({ categoryName, data }) => {
  // Этот компонент отображается только для родительских категорий,
  // у которых есть дочерние .up и .down
  if (!data || !data.up || !data.down || data.up.count === 0 || data.down.count === 0) {
    // Для дочерних категорий или пустых родителей ничего не показываем
    return null;
  }

  // Внутренний компонент для одной строки сравнения
  const ComparisonStat = ({ label, upData, downData, isPercent = false }) => {
    // Безопасно извлекаем нужные нам средние и медианы
    const upAvg = upData?.avg || 0;
    const upMedian = upData?.median || 0;
    const downAvg = downData?.avg || 0;
    const downMedian = downData?.median || 0;

    // Рассчитываем отношение по средним значениям
    const ratioAvg = downAvg !== 0 ? upAvg / downAvg : 0;
    const ratioMedian = downMedian !== 0 ? upMedian / downMedian : 0;

    return (
      <Grid item xs={6} sm={2}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6" component="p" sx={{ mb: 0.5 }}>
          Avg: {formatValue(ratioAvg, 2)} | Med: {formatValue(ratioMedian, 2)}
        </Typography>
        <Box>
          <Typography variant="body2" sx={{ color: 'success.main' }}>
            Up: <strong>{formatValue(upAvg, 3, isPercent)}</strong> (avg) / {formatValue(upMedian, 3, isPercent)} (med)
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            Down: <strong>{formatValue(downAvg, 3, isPercent)}</strong> (avg) / {formatValue(downMedian, 3, isPercent)} (med)
          </Typography>
        </Box>
      </Grid>
    );
  };

  return (
    <Card sx={{ mb: 1, border: '1px solid', borderColor: 'primary.light' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Comparison Up/Down Ratio: {categoryName}</Typography>
        <Grid container spacing={1} sx={{ mt: 0 }} rowSpacing={1}>
          <ComparisonStat
            label="Up/Dn Delta (%)"
            upData={data.up?.deltaPercent}
            downData={data.down?.deltaPercent}
            isPercent={true}
          />
          <ComparisonStat
            label="Up/Dn PrevMult (xRatio)"
            upData={data.up?.deltaRatioPrev}
            downData={data.down?.deltaRatioPrev}
          />
          <ComparisonStat
            label="Up/Dn Wave Volume"
            upData={data.up?.totalVolume}
            downData={data.down?.totalVolume}
          />
          <ComparisonStat
            label="Up/Dn Effort Ratio"
            upData={data.up?.effortRatio}
            downData={data.down?.effortRatio}
          />
          <ComparisonStat
            label="Up/Dn Max Inner Diff %"
            upData={data?.maxDiffUpPercent}
            downData={data?.maxDiffDnPercent}
            isPercent={true}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

const KeyIndicatorsCard = ({ categoryName, data }) => {
  if (!data || !data.count) return null;
  return (
    <Card sx={{ mb: 1, border: '1px solid', borderColor: 'primary.light' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Main Indicators: {categoryName}</Typography>
        <Grid container spacing={1} sx={{ mt: 0 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" display="block">Median Delta (%)</Typography>
            <Typography variant="h6" component="p">{formatValue(data.deltaPercent.median, 2, true)}%</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" display="block">Average Current / Prev Delta (xRatio)</Typography>
            <Typography variant="h6" component="p">{formatValue(data.deltaRatioPrev.avg, 2)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" display="block">Average Volume</Typography>
            <Typography variant="h6" component="p">{formatValue(data.totalVolume.avg, 3)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" display="block">Average Effort Ratio</Typography>
            <Typography variant="h6" component="p">{formatValue(data.effortRatio.avg, 3)}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const MetricCard = ({ name, data }) => {
  if (!data || !data.count) return null;
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography sx={{ fontSize: 14, fontWeight: 'bold' }} color="text.secondary" gutterBottom>
            {name}
          </Typography>
          <Typography variant="body2">
            Min: {formatValue(data.min, 3)}, Max: {formatValue(data.max, 3)}
          </Typography>
          <Typography variant="body2">
            Avg: <strong>{formatValue(data.avg, 3)}</strong>,
            Median: <strong>{formatValue(data.median, 3)}</strong>
          </Typography>
          <Typography variant="body2">
            StdDev: {formatValue(data.stdDev, 3)}, MAD: {formatValue(data.mad, 3)}
          </Typography>

          {data.distribution ? <Box mt={1}>
            <Typography variant="caption" display="block">
              Dist Std: [{data.distribution.stdDev.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
            <Typography variant="caption" display="block">
              NegD Std: [{data.distribution.stdDevNeg.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
            <Typography variant="caption" display="block">
              PosD Std: [{data.distribution.stdDevPos.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
            <Typography variant="caption" display="block">
              Dist MAD: [{data.distribution.mad.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
            <Typography variant="caption" display="block">
              NegD MAD: [{data.distribution.madNeg.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
            <Typography variant="caption" display="block">
              PosD MAD: [{data.distribution.madPos.map(v => (v * 100).toFixed(1)).join('%, ')}%]
            </Typography>
          </Box> : null}
        </CardContent>
      </Card>
    </Grid>
  );
};


const CategoryDetailView = ({ data, categoryName }) => {
  const [detailsVisible, setDetailsVisible] = useState(false);

  if (!data) return null;

  const isParentCategory = data.up && data.down;

  return (
    <Box sx={{ p: 1, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>

      {isParentCategory
        ? <ComparisonCard categoryName={categoryName} data={data} />
        : <KeyIndicatorsCard categoryName={categoryName} data={data} />
      }

      <Collapse in={detailsVisible} timeout="auto" unmountOnExit>
        <Grid container spacing={1}>
          <MetricCard name="Delta %" data={data.deltaPercent} />
          <MetricCard name="Delta Ratio Prev" data={data.deltaRatioPrev} />
          <MetricCard name="Duration" data={data.duration} />
          <MetricCard name="Effort Ratio" data={data.effortRatio} />
          <MetricCard name="Delta" data={data.delta} />
          <MetricCard name="Max Diff Up %" data={data.maxDiffUpPercent} />
          <MetricCard name="Max Diff Down %" data={data.maxDiffDnPercent} />
          <MetricCard name="Total Volume" data={data.totalVolume} />
        </Grid>
      </Collapse>

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Button
          size="small"
          onClick={() => setDetailsVisible(prev => !prev)}
        >
          {detailsVisible ? 'Hide Details' : 'Show Details'}
        </Button>
      </Box>

    </Box>
  );
};

export const StatsSummaryTable = ({ stats }) => {
  // 1. ИЗМЕНЕНИЕ: Вместо null, инициализируем состояние пустым массивом
  const [expandedRowIds, setExpandedRowIds] = useState([]);

  // 2. ИЗМЕНЕНИЕ: Новая логика для обработки кликов
  const handleRowClick = (rowId) => {
    setExpandedRowIds(prevExpanded => {
      const isCurrentlyExpanded = prevExpanded.includes(rowId);
      if (isCurrentlyExpanded) {
        // Если строка уже открыта, удаляем ее ID из массива, чтобы закрыть
        return prevExpanded.filter(id => id !== rowId);
      } else {
        // Если строка закрыта, добавляем ее ID в массив, чтобы открыть
        return [...prevExpanded, rowId];
      }
    });
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Typography variant="h6" sx={{ p: 2, fontWeight: 'bold' }}>
        Waves Stats
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columnsConfig.map((column) => (
              <TableCell key={column.id} align={column.align}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsConfig.map((row) => {
            const rowData = row.dataAccessor(stats);
            if (!rowData || !rowData.count) return null;

            // 3. ИЗМЕНЕНИЕ: Проверяем наличие ID в массиве через .includes()
            const isExpanded = expandedRowIds.includes(row.id);

            return (
              <React.Fragment key={row.id}>
                <TableRow
                  hover
                  onClick={() => handleRowClick(row.id)}
                  selected={isExpanded}
                  sx={{ cursor: 'pointer', backgroundColor: row.level > 0 ? 'rgba(255, 255, 255, 0.04)' : 'transparent' }}
                >
                  {columnsConfig.map((column) => (
                    <TableCell
                      key={`${row.id}-${column.id}`}
                      align={column.align}
                      component={column.id === 'name' ? 'th' : 'td'}
                      scope={column.id === 'name' ? 'row' : undefined}
                      sx={{ paddingLeft: column.id === 'name' ? `${1 + (row.level || 0) * 1.5}rem` : undefined }}
                    >
                      {column.formatter(rowData, row)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={{ padding: 0, borderBottom: 'none' }} colSpan={columnsConfig.length}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <CategoryDetailView data={rowData} categoryName={row.label} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const WaveStatsDisplay = () => {
  const optimalWaves = useRecoilValue(optimalWavesState);
  const statsData = useRecoilValue(simpleStatsState);
  const waveClusterStats = useRecoilValue(waveClusterStatsState)
  const waveClusterOutcomes = useRecoilValue(waveClusterOutcomesState)

  if (statsData === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Calculating...</Typography>
      </Box>
    );
  }
  if (!statsData.overall?.count) {
    return <Typography sx={{ p: 4, textAlign: 'center' }}>No Data Available</Typography>;
  }
  return (
    <Box sx={{ p: 2 }}>
      <StrategyDashboard stats={statsData} />
      <ConfigurableComparisons stats={statsData} config={comparisonConfig} />
      <StatsSummaryTable stats={statsData} />
    </Box>
  );
};

export default WaveStatsDisplay;